/**
 * Keyboard focus for the rig's two modal dialogs, the QR Reader and the
 * Registration QR overlay (Codex rounds 2 and 3 of the phone design pass).
 * While one is open: everything outside it is inert (no Tab, no click, no
 * screen reader), Tab and Shift+Tab stay inside it wherever focus was, focus
 * that drops out of it (a control that disappears or turns disabled, a tap on
 * plain text) comes back to its frame, and the armed screen's Space/Enter
 * shortcut stays quiet. When it closes, focus goes back to the button that
 * opened it. The pure parts take the DOM as arguments, so the test can use
 * stand-ins; useModalDialog wires them to a mounted dialog.
 */
import { useEffect } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

// The controls a keyboard can reach inside the dialog, in order. A control the
// CSS hides at this width is not one (the reader has a Close for each layout
// and shows one).
export function focusableIn(container) {
  if (!container || typeof container.querySelectorAll !== "function") return [];
  return Array.from(container.querySelectorAll(FOCUSABLE))
    .filter((el) => typeof el.getClientRects !== "function" || el.getClientRects().length > 0);
}

// Tab from the last control goes to the first, Shift+Tab from the first (or
// from the dialog's own frame, where focus lands when it opens) goes to the
// last, and a Tab from anywhere outside, the page body included, comes back
// in. Any other Tab is the browser's. Returns true when it moved focus itself.
export function keepTabInside(event, container, active) {
  if (!event || event.key !== "Tab" || !container) return false;
  const items = focusableIn(container);
  if (items.length === 0) {
    event.preventDefault();
    if (typeof container.focus === "function") container.focus();
    return true;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const inside = Boolean(active) && typeof container.contains === "function" && container.contains(active);
  if (event.shiftKey) {
    if (!inside || active === first || active === container || !items.includes(active)) {
      event.preventDefault();
      last.focus();
      return true;
    }
    return false;
  }
  if (!inside || active === last || (active !== container && !items.includes(active))) {
    event.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

// True while a modal dialog is open anywhere on the page.
export function modalOpen(doc = globalThis.document) {
  return Boolean(doc && typeof doc.querySelector === "function" && doc.querySelector('[aria-modal="true"]'));
}

// Makes everything outside the dialog inert: at each level from the dialog up
// to <body>, every sibling of the dialog's branch. The dialog and its
// ancestors stay live. Elements that were inert already are left alone, and
// the function returned undoes exactly what it did.
export function inertOutside(dialog) {
  const changed = [];
  const body = dialog?.ownerDocument?.body ?? null;
  for (let node = dialog; node && node !== body && node.parentElement; node = node.parentElement) {
    for (const sibling of Array.from(node.parentElement.children)) {
      if (sibling === node || sibling.tagName === "SCRIPT" || sibling.hasAttribute("inert")) continue;
      sibling.setAttribute("inert", "");
      changed.push(sibling);
    }
  }
  return () => { for (const el of changed) el.removeAttribute("inert"); };
}

// Focus is outside the dialog (or nowhere, on the body) while it is open.
export function focusLost(container, active, body) {
  if (!container) return false;
  return !active || active === body || !(typeof container.contains === "function" && container.contains(active));
}

/**
 * Wires a mounted modal dialog: inert outside, focus in (initialFocusRef, or
 * the dialog frame, which needs tabIndex={-1}), Tab kept inside from anywhere,
 * lost focus recovered, and on unmount focus back to returnFocusRef, else to
 * what had focus before, else to the rig's current tab (a stable button in the
 * shell, for when the screen under the dialog was replaced while it was open:
 * the rig armed from another device). Runs once per mount.
 */
export function useModalDialog(containerRef, { initialFocusRef = null, returnFocusRef = null } = {}) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const doc = container.ownerDocument;
    const before = doc.activeElement;
    // The button that opened the dialog: the same DOM node for as long as it
    // stays on screen, so it is read once here.
    const launcher = returnFocusRef?.current ?? null;
    const shell = typeof container.closest === "function" ? container.closest(".rigv2-shell") : null;
    const restoreInert = inertOutside(container);
    const focusFrame = () => { if (container.isConnected) container.focus({ preventScroll: true }); };
    const start = initialFocusRef?.current;
    if (start && typeof start.focus === "function") start.focus({ preventScroll: true });
    else focusFrame();

    const recover = () => { if (focusLost(container, doc.activeElement, doc.body)) focusFrame(); };
    const onKey = (e) => { if (e.key === "Tab") keepTabInside(e, container, doc.activeElement); };
    const onFocusIn = (e) => { if (!container.contains(e.target)) focusFrame(); };
    // A control that disappears or turns disabled takes focus with it to the
    // body; bring it back to the frame once the DOM has settled.
    const observer = typeof MutationObserver === "function"
      ? new MutationObserver(() => { if (focusLost(container, doc.activeElement, doc.body)) queueMicrotask(recover); })
      : null;
    if (observer) observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled"] });
    doc.addEventListener("keydown", onKey, true);
    doc.addEventListener("focusin", onFocusIn, true);

    return () => {
      doc.removeEventListener("keydown", onKey, true);
      doc.removeEventListener("focusin", onFocusIn, true);
      if (observer) observer.disconnect();
      restoreInert();
      const usable = (el) => Boolean(el) && el !== doc.body && el.isConnected && typeof el.focus === "function";
      const target = [launcher, before, shell?.querySelector(".rigv2-tab.active")].find(usable);
      if (target) target.focus({ preventScroll: true });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
